import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent } from 'react'
import clsx from 'clsx'
import { Check, Plus } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'
import type { ColumnTag } from '../schemas'
import type { TagCellDraft } from '../hooks/useTableCellDrafts'
import TableTag from './TableTag'
import TagCellSelections from './TagCellSelections'
import { tagContent, tagItems } from '../editor/tag-content'
import { leastUsedTagColor } from '../utils/tag-colors'

/** Tag selections retain per-label citations and share the cell save/conflict checks. */
export default function TagCellEditor({
  tags,
  allowMultiple,
  draft,
  onDraftChange,
  label,
  saving,
  onSave,
  onCancel,
  autoFocus,
  canCreate = false,
  matterId,
}: {
  tags: ColumnTag[]
  allowMultiple: boolean
  autoFocus: boolean
  draft: TagCellDraft
  onDraftChange: (draft: TagCellDraft) => void
  label: string
  saving: boolean
  onSave: () => void
  onCancel: () => void
  canCreate?: boolean
  matterId?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const anchor = useRef<HTMLDivElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const cancelled = useRef(false)
  const latest = useRef({ onSave, saving })
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(autoFocus)
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const id = useId()
  useLayoutEffect(() => {
    latest.current = { onSave, saving }
  })
  const isInside = (target: Node) => {
    const cell = anchor.current?.closest('td') ?? anchor.current
    return cell?.contains(target) || menu.current?.contains(target)
  }
  useEffect(() => {
    const saveOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !isInside(event.target) && !cancelled.current && !latest.current.saving) latest.current.onSave()
    }
    document.addEventListener('pointerdown', saveOutside, true)
    return () => document.removeEventListener('pointerdown', saveOutside, true)
  }, [])
  const blur = (event: FocusEvent) => {
    if (
      event.relatedTarget instanceof Node &&
      !event.currentTarget.contains(event.relatedTarget) &&
      !isInside(event.relatedTarget) &&
      !cancelled.current &&
      !saving
    )
      onSave()
  }
  const cancel = () => {
    if (saving) return
    cancelled.current = true
    onCancel()
  }
  useLayoutEffect(() => {
    if (autoFocus) input.current?.focus({ preventScroll: true })
  }, [autoFocus])

  const options = [...tags, ...(draft.newTags ?? []).filter((tag) => !tags.some((existing) => existing.label === tag.label))]
  const values = draft.content.map((segment) => segment.text).filter(Boolean)
  const search = query.trim().toLocaleLowerCase()
  const filtered = options.filter((tag) => tag.label.toLocaleLowerCase().startsWith(search))
  const activeIndex = filtered.findIndex((tag) => tag.label === activeLabel)
  const showCreate = canCreate && search.length > 0 && !options.some((tag) => tag.label.toLocaleLowerCase() === search)
  const newTag: ColumnTag = { label: query.trim(), color: leastUsedTagColor(options), custom_color: null }

  const choose = (labels: string[], nextOptions = options, newTags = draft.newTags) => {
    if (saving) return
    const items = tagItems(draft.content).filter((item) => item.kind === 'reference' || labels.includes(item.value))
    for (const option of nextOptions.filter((option) => labels.includes(option.label))) {
      if (items.some((item) => item.kind === 'tag' && item.value === option.label)) continue
      items.push({ kind: 'tag', value: option.label })
    }
    onDraftChange({
      ...draft,
      newTags,
      content: tagContent(items),
      needsVerification: false,
    })
  }
  const toggle = (value: string) => {
    choose(values.includes(value) ? values.filter((selected) => selected !== value) : allowMultiple ? [...values, value] : [value])
    setQuery('')
    setActiveLabel(null)
    input.current?.focus({ preventScroll: true })
  }
  const create = () => {
    if (!showCreate || saving) return
    choose(allowMultiple ? [...values, newTag.label] : [newTag.label], [...options, newTag], [...(draft.newTags ?? []), newTag])
    setQuery('')
    setActiveLabel(null)
    input.current?.focus({ preventScroll: true })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchor} onBlur={blur}>
          <TagCellSelections options={options} draft={draft} matterId={matterId} saving={saving} onDraftChange={onDraftChange} onRemoveTag={toggle}>
            <input
              ref={input}
              role="combobox"
              aria-label={`${allowMultiple ? 'Values' : 'Value'} for ${label}`}
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={open ? `${id}-options` : undefined}
              aria-activedescendant={open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
              autoComplete="off"
              disabled={saving}
              placeholder={values.length ? '' : 'Select tags…'}
              className="h-6 w-16 min-w-12 flex-1 border-0 bg-transparent p-0 text-sm text-brand-neutral-950 outline-none placeholder:text-brand-neutral-500 focus:ring-0"
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(event) => {
                setQuery(event.target.value)
                setActiveLabel(null)
                setOpen(true)
              }}
              onKeyDown={(event) => {
                if (event.nativeEvent.isComposing) return
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault()
                  setOpen(true)
                  const next = event.key === 'ArrowDown' ? (activeIndex + 1) % filtered.length : (activeIndex <= 0 ? filtered.length : activeIndex) - 1
                  setActiveLabel(filtered[next]?.label ?? null)
                  document.getElementById(`${id}-option-${next}`)?.scrollIntoView({ block: 'nearest' })
                } else if (event.key === 'Backspace' && query === '') {
                  event.preventDefault()
                  if (event.repeat || saving) return
                  const items = tagItems(draft.content)
                  const last = items.at(-1)
                  if (last?.kind === 'tag') toggle(last.value)
                  else if (last) onDraftChange({ ...draft, content: tagContent(items.slice(0, -1)) })
                } else if (event.key === 'Enter') {
                  event.preventDefault()
                  if (activeIndex >= 0) toggle(filtered[activeIndex].label)
                  else if (showCreate && !filtered.length) create()
                  else if (filtered.length && search) toggle(filtered[0].label)
                  else onSave()
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  cancel()
                }
              }}
            />
          </TagCellSelections>
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={menu}
        onBlur={blur}
        aria-label={`Tags for ${label}`}
        align="start"
        width="auto"
        showArrow={false}
        sideOffset={6}
        collisionPadding={8}
        className="flex w-[max(240px,var(--radix-popover-trigger-width))] max-w-[calc(100vw-16px)] max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden border border-brand-neutral-200 p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          event.preventDefault()
          cancel()
        }}
      >
        <div
          id={`${id}-options`}
          role="listbox"
          aria-label={`Tags for ${label}`}
          aria-multiselectable={allowMultiple}
          className="min-h-0 overflow-y-auto p-1.5"
        >
          {filtered.map((tag, index) => (
            <Button
              key={tag.label}
              id={`${id}-option-${index}`}
              type="button"
              role="option"
              aria-label={tag.label}
              aria-selected={values.includes(tag.label)}
              disabled={saving}
              variant="plain"
              color="neutral"
              size="sm"
              tabIndex={-1}
              className={clsx(
                '!h-auto min-h-9 w-full !justify-start !gap-2.5 !px-2 !py-1.5',
                (values.includes(tag.label) || activeIndex === index) && '!bg-brand-100/50'
              )}
              onClick={() => toggle(tag.label)}
            >
              <span
                aria-hidden
                className={clsx(
                  'flex size-4 shrink-0 items-center justify-center rounded border',
                  values.includes(tag.label) ? 'border-brand-500 bg-brand-500 text-white' : 'border-brand-neutral-300 bg-white'
                )}
              >
                {values.includes(tag.label) && <Check className="size-3" />}
              </span>
              <TableTag tag={tag}>
                <span className="text-left">{tag.label}</span>
              </TableTag>
            </Button>
          ))}
          {!filtered.length && <p className="px-2 py-2 text-xs text-brand-neutral-500">{search ? 'No matching tags' : 'No tags yet'}</p>}
        </div>
        {showCreate && (
          <div className="border-t border-brand-neutral-200 p-1.5">
            <Button
              type="button"
              aria-label={`Create tag ${newTag.label}`}
              variant="plain"
              color="secondary"
              size="sm"
              disabled={saving}
              className="!h-auto min-h-9 w-full !justify-start !gap-2.5 !px-2 !py-1.5 !font-normal"
              onClick={create}
            >
              <Plus aria-hidden className="size-4 shrink-0 text-brand-500" />
              <span className="shrink-0">Create tag</span>
              <TableTag tag={newTag}>
                <span className="text-left">{newTag.label}</span>
              </TableTag>
            </Button>
          </div>
        )}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-brand-neutral-200 px-3.5 py-2">
          <span role="status" className="text-xs text-brand-neutral-600">
            {values.length} selected
          </span>
          <Button
            type="button"
            size="sm"
            disabled={saving || (!allowMultiple && values.length > 1) || values.some((value) => !options.some((tag) => tag.label === value))}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Done'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
